import React from 'react';
import { Box, Heading } from '@wix/design-system';
import { RecommendationLabel } from '@wix/ambassador-premium-domains-v1-recommendation/types';
import { parseDomain } from '@wix/domains-serverless-interface';
import { AiInsightsButton, ComBrRestriction, DomainSearchSuiteModule, DomainText, PremiumDomainBadge, PricingSection, isInteractiveClickTarget } from '@wix/domains-search-suite';
import { useIsMobile } from '../../hooks/useIsMobile';
import { HeroInsightBullets } from '../HeroInsightBullets';
import { HeroBusinessEmailExample } from './HeroBusinessEmailExample';
import { AvailableNowBadge } from './AvailableNowBadge';
import { Confetti } from './Confetti';
import { hasMatchLabel, MatchLabelBadge } from './MatchLabelBadge';
import { CompactHeroCard } from './CompactHeroCard';
import { HeroCtaButton } from './HeroCtaButton';
import { StackedMobileCard } from './StackedMobileCard';
import s from './HeroCard.css';
const SCALED_TITLE_MIN_FONT_SIZE = '1.25rem';
export const HeroCard = _ref => {
  let recommendation = _ref.recommendation,
    impressionReport = _ref.impressionReport,
    onClickGetIt = _ref.onClickGetIt,
    onItemClick = _ref.onItemClick,
    onAiInsightsClick = _ref.onAiInsightsClick,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    _ref$dataHook = _ref.dataHook,
    dataHook = _ref$dataHook === void 0 ? 'hero-card' : _ref$dataHook,
    _ref$useCompactHeroCa = _ref.useCompactHeroCardLayout,
    useCompactHeroCardLayout = _ref$useCompactHeroCa === void 0 ? false : _ref$useCompactHeroCa,
    _ref$desktopContentPa = _ref.desktopContentPadding,
    desktopContentPadding = _ref$desktopContentPa === void 0 ? 'SP6' : _ref$desktopContentPa,
    _ref$desktopContentGa = _ref.desktopContentGap,
    desktopContentGap = _ref$desktopContentGa === void 0 ? '10px' : _ref$desktopContentGa,
    _ref$insightsPaddingT = _ref.insightsPaddingTop,
    insightsPaddingTop = _ref$insightsPaddingT === void 0 ? 'SP2' : _ref$insightsPaddingT,
    _ref$compactPriceLabe = _ref.compactPriceLabel,
    compactPriceLabel = _ref$compactPriceLabe === void 0 ? false : _ref$compactPriceLabe,
    Label = _ref.Label,
    mainCardBadgeClassNames = _ref.mainCardBadgeClassNames,
    _ref$compact = _ref.compact,
    compact = _ref$compact === void 0 ? false : _ref$compact,
    badgeLabel = _ref.badgeLabel,
    _ref$ctaPriority = _ref.ctaPriority,
    ctaPriority = _ref$ctaPriority === void 0 ? 'primary' : _ref$ctaPriority,
    _ref$inlineMobileActi = _ref.inlineMobileActions,
    inlineMobileActions = _ref$inlineMobileActi === void 0 ? false : _ref$inlineMobileActi,
    _ref$stackedMobileLay = _ref.stackedMobileLayout,
    stackedMobileLayout = _ref$stackedMobileLay === void 0 ? false : _ref$stackedMobileLay,
    _ref$businessEmailExa = _ref.businessEmailExample,
    businessEmailExample = _ref$businessEmailExa === void 0 ? false : _ref$businessEmailExa,
    _ref$isPricingLoading = _ref.isPricingLoading,
    isPricingLoading = _ref$isPricingLoading === void 0 ? false : _ref$isPricingLoading,
    _ref$titleOverflowStr = _ref.titleOverflowStrategy,
    titleOverflowStrategy = _ref$titleOverflowStr === void 0 ? 'ellipsis' : _ref$titleOverflowStr;
  const isMobile = useIsMobile();
  const _recommendation$domai = recommendation.domain,
    domain = _recommendation$domai === void 0 ? '' : _recommendation$domai,
    label = recommendation.label,
    _recommendation$premi = recommendation.premium,
    premium = _recommendation$premi === void 0 ? false : _recommendation$premi,
    price = recommendation.price;
  const _parseDomain = parseDomain(domain),
    tld = _parseDomain.tld;
  const isExactMatch = label === RecommendationLabel.EXACT_MATCH;
  const showClickableCardStyling = !!onItemClick && !isMobile;
  const isStackedMobile = stackedMobileLayout && isMobile;
  const impressionRef = DomainSearchSuiteModule.SDK.useSuggestionImpression({
    domain: recommendation.domain,
    position: (impressionReport == null ? void 0 : impressionReport.position) ?? 0,
    pageNumber: (impressionReport == null ? void 0 : impressionReport.pageNumber) ?? 1,
    isPremium: recommendation.premium,
    enabled: !!impressionReport
  });
  if (compact) {
    return /*#__PURE__*/React.createElement(CompactHeroCard, {
      rootRef: impressionRef,
      recommendation: recommendation,
      onClickGetIt: onClickGetIt,
      onItemClick: onItemClick,
      disabled: disabled,
      dataHook: dataHook,
      useCompactHeroCardLayout: useCompactHeroCardLayout,
      stackedMobileLayout: stackedMobileLayout,
      Label: Label,
      mainCardBadgeClassNames: mainCardBadgeClassNames,
      badgeLabel: badgeLabel,
      ctaPriority: ctaPriority,
      businessEmailExample: businessEmailExample,
      isPricingLoading: isPricingLoading,
      compactPriceLabel: compactPriceLabel,
      titleOverflowStrategy: titleOverflowStrategy
    });
  }
  const showMatchBadge = hasMatchLabel(label) && Boolean(label);
  const ctaButton = /*#__PURE__*/React.createElement(HeroCtaButton, {
    size: isMobile && !isStackedMobile ? 'medium' : 'large',
    priority: ctaPriority,
    disabled: disabled,
    fullWidth: isStackedMobile,
    onClick: () => onClickGetIt == null ? void 0 : onClickGetIt(recommendation),
    dataHook: `${dataHook}-cta`
  });
  const badgesRow = /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP2",
    verticalAlign: "middle",
    flexWrap: "wrap"
  }, showMatchBadge && label && (Label ? /*#__PURE__*/React.createElement(Label, {
    primary: true,
    label: label,
    domain: domain,
    size: isMobile ? 'tiny' : 'small'
  }) : /*#__PURE__*/React.createElement(MatchLabelBadge, {
    label: label,
    optimized: false
  })), isExactMatch && /*#__PURE__*/React.createElement(AvailableNowBadge, null), premium && /*#__PURE__*/React.createElement(PremiumDomainBadge, {
    size: isMobile ? 'tiny' : 'small'
  }));
  const scalesTitleToFit = titleOverflowStrategy === 'scale';
  const scaledTitleMaxFontSize = isStackedMobile ? '1.75rem' : isMobile ? '1.875rem' : '2rem';
  const titleHeading = useCompactHeroCardLayout ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px",
    className: scalesTitleToFit ? s.titleColumn : undefined
  }, /*#__PURE__*/React.createElement("span", {
    className: s.DomainTitleWrapper
  }, /*#__PURE__*/React.createElement(DomainText, {
    overflowStrategy: titleOverflowStrategy,
    minFontSize: SCALED_TITLE_MIN_FONT_SIZE,
    maxFontSize: scaledTitleMaxFontSize,
    maxWidth: isMobile ? '310px' : '440px',
    textComponent: Heading,
    textComponentProps: {
      as: 'h1',
      size: isMobile ? 'large' : 'extraLarge'
    },
    className: `${s.optimizedHeroTitle}${isStackedMobile ? ` ${s.stackedMobileTitle}` : ''}`,
    dataHook: `${dataHook}-title`
  }, domain), isExactMatch && /*#__PURE__*/React.createElement(Confetti, null)), isExactMatch && /*#__PURE__*/React.createElement(ComBrRestriction, {
    domainName: domain
  })) : /*#__PURE__*/React.createElement(Heading, {
    size: isMobile ? 'large' : 'extraLarge',
    as: "h1",
    dataHook: `${dataHook}-title`
  }, /*#__PURE__*/React.createElement("span", {
    className: s.DomainTitleWrapper
  }, /*#__PURE__*/React.createElement("span", {
    style: {
      fontWeight: isMobile ? 600 : 500
    }
  }, domain), isExactMatch && /*#__PURE__*/React.createElement(Confetti, null)));
  const pricingSection = /*#__PURE__*/React.createElement(PricingSection, {
    pricing: price,
    tld: tld,
    domainName: domain,
    useMobileLayout: isMobile,
    compactPriceLabel: compactPriceLabel,
    useIntroPriceLabelCopy: compactPriceLabel && useCompactHeroCardLayout,
    denseStrikethroughStyle: useCompactHeroCardLayout,
    matchStrikethroughSize: isMobile && useCompactHeroCardLayout,
    isPricingLoading: isPricingLoading
  });
  const stackedMobileSection = /*#__PURE__*/React.createElement(StackedMobileCard, {
    spacious: isExactMatch,
    badges: badgesRow,
    title: titleHeading,
    bullets: isExactMatch && /*#__PURE__*/React.createElement(HeroInsightBullets, {
      domain: domain,
      size: "small"
    }),
    pricing: pricingSection,
    cta: ctaButton
  });
  const topSection = /*#__PURE__*/React.createElement(Box, {
    direction: isMobile ? 'vertical' : 'horizontal',
    padding: isMobile ? 'SP6 SP6 16px SP6' : desktopContentPadding,
    align: isMobile ? undefined : 'space-between',
    gap: isMobile ? useCompactHeroCardLayout ? '16px' : '10px' : 'SP6'
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: isMobile ? useCompactHeroCardLayout ? '16px' : 'SP6' : desktopContentGap,
    flex: isMobile ? undefined : 1,
    minWidth: 0
  }, badgesRow, businessEmailExample ? /*#__PURE__*/React.createElement(Box, {
    direction: "vertical",
    gap: "4px"
  }, titleHeading, !isMobile && /*#__PURE__*/React.createElement(HeroBusinessEmailExample, {
    domain: domain
  })) : /*#__PURE__*/React.createElement(React.Fragment, null, titleHeading, !isMobile && isExactMatch && /*#__PURE__*/React.createElement(Box, {
    paddingTop: insightsPaddingTop
  }, /*#__PURE__*/React.createElement(HeroInsightBullets, {
    domain: domain
  })))), /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    gap: "SP4",
    verticalAlign: "middle",
    flexShrink: 0,
    width: isMobile ? '100%' : undefined,
    align: isMobile && (useCompactHeroCardLayout || inlineMobileActions) ? 'space-between' : undefined,
    flexWrap: isMobile && (useCompactHeroCardLayout || inlineMobileActions) ? 'wrap' : undefined,
    alignSelf: isMobile ? undefined : 'flex-end'
  }, pricingSection, (!isMobile || useCompactHeroCardLayout || inlineMobileActions) && /*#__PURE__*/React.createElement(Box, {
    flexShrink: 0,
    marginLeft: isMobile && (useCompactHeroCardLayout || inlineMobileActions) ? 'auto' : undefined
  }, ctaButton)), isMobile && !useCompactHeroCardLayout && !inlineMobileActions && /*#__PURE__*/React.createElement(Box, {
    direction: "horizontal",
    align: "space-between",
    verticalAlign: "middle"
  }, onAiInsightsClick ? /*#__PURE__*/React.createElement(AiInsightsButton, {
    onClick: () => onAiInsightsClick(domain)
  }) : /*#__PURE__*/React.createElement(Box, null), ctaButton));
  const cardSection = isStackedMobile ? stackedMobileSection : topSection;
  const clickableCardSection = onItemClick ? /*#__PURE__*/React.createElement("div", {
    className: s.cardClickArea,
    "data-hook": `${dataHook}-click-area`,
    onClick: event => {
      if (disabled || isInteractiveClickTarget(event.target)) {
        return;
      }
      onItemClick(recommendation);
    }
  }, cardSection) : cardSection;
  return /*#__PURE__*/React.createElement(Box, {
    ref: impressionRef,
    direction: "vertical",
    width: "100%",
    borderRadius: "12px",
    overflow: "hidden",
    className: [useCompactHeroCardLayout ? s.card : s.cardShadow, showClickableCardStyling && s.clickableCard].filter(Boolean).join(' '),
    dataHook: dataHook
  }, clickableCardSection);
};
//# sourceMappingURL=HeroCard.js.map